import { Anchor } from '@daily/shared/components/Anchor';
import { Button } from '@daily/shared/components/Button';
import { CloseIcon } from '@daily/shared/components/Icons';
import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { VisuallyHidden } from '@daily/shared/components/VisuallyHidden';
import { useTheme } from '@daily/shared/contexts/Theme';
import classNames from 'classnames';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import Linkify from 'react-linkify';
import { atom, useRecoilValue } from 'recoil';
import { useRecoilCallback } from 'recoil';
import tinykeys from 'tinykeys';
import { useDeepCompareEffect } from 'use-deep-compare';

import { ChatMessage as IChatMessage } from '../../contexts/ChatProvider';
import { useStyleVariants } from '../../hooks/useStyleVariants';
import { ChatReactions } from './ChatReactions';
import { GiphyGif } from './GiphyGif';
import { GiphyIcon } from './GiphyIcon';

interface Props {
  enableAdvancedChat: boolean;
  message: IChatMessage;
  isLocal: boolean;
  onRender?(id: string, el: HTMLDivElement): void;
  showSender?: boolean;
}

const MsgLink = (href, text, key) => (
  <Anchor href={href} key={key} target="_blank" underline>
    {text}
  </Anchor>
);

const chatImageHeightsState = atom<Record<string, number>>({
  key: 'chat-image-heights',
  default: {},
});

export const ChatMessage: React.FC<Props> = memo(
  ({ enableAdvancedChat, message, isLocal, onRender, showSender = false }) => {
    const { t } = useTranslation();
    const { colors, fontSizes, lineHeights, mediaQueries } = useTheme();
    const msgRef = useRef<HTMLDivElement>(null);
    const { textVariantSmall } = useStyleVariants();

    const [showReactions, setShowReactions] = useState(false);

    const imgMatch = message.message.match(/!\[(.*)\]\((.*)\)/);
    const giphyMatch = message.message.match(/!\[(.+)-giphy\]\((.*)\)/);

    const reactionsCount = Object.values(message.reactions ?? {}).reduce(
      (sum, { count }) => sum + count,
      0
    );

    const handleAddReactionClick = () => {
      setShowReactions((show) => !show);
    };

    const handleReaction = () => {
      setShowReactions(false);
    };

    /**
     * Setup focus shift when closing reactions.
     */
    useEffect(() => {
      if (!showReactions) return;

      const unsubscribe = tinykeys(document.body, {
        Escape: (ev) => {
          ev.stopImmediatePropagation();
          ev.preventDefault();
          setShowReactions(false);
          (
            msgRef.current.querySelector('.add-reaction') as HTMLButtonElement
          )?.focus();
        },
      });

      return () => {
        unsubscribe();
      };
    }, [showReactions]);

    /**
     * Call onRender to inform parent about potentially changed DOM.
     */
    useDeepCompareEffect(() => {
      onRender?.(message?.id, msgRef.current);
    }, [
      message?.id,
      message?.message,
      message?.name,
      message?.reactions,
      onRender,
      showReactions,
      showSender,
    ]);

    const imgHeights = useRecoilValue(chatImageHeightsState);
    const handleGifLoad = useRecoilCallback(
      ({ set }) =>
        (ev: React.SyntheticEvent<HTMLImageElement, Event>) => {
          set(chatImageHeightsState, (prev) => ({
            ...prev,
            [message.id]: ev.currentTarget.clientHeight,
          }));
          onRender?.(message?.id, msgRef.current);
        },
      [message?.id, onRender]
    );

    return (
      <div
        className={classNames('chat-message', {
          'with-sender': showSender,
        })}
        ref={msgRef}
      >
        <div
          className={classNames('msg', {
            isLocal,
            gif: !!imgMatch,
          })}
        >
          {imgMatch ? (
            <Stack align="stretch" gap={2} width="100%">
              <GiphyGif
                height={imgHeights?.[message.id]}
                onLoad={handleGifLoad}
                title={imgMatch[1]}
                src={imgMatch[2]}
              />
              {giphyMatch ? (
                <Text
                  className="powered-by-giphy"
                  color="muted"
                  variant="small"
                >
                  <GiphyIcon /> Powered by GIPHY
                </Text>
              ) : null}
            </Stack>
          ) : (
            <Linkify componentDecorator={MsgLink}>
              {message.message.split('\n').map((line, i) => (
                <Fragment key={`line-${line}`}>
                  {i > 0 && <br />}
                  {line}
                </Fragment>
              ))}
            </Linkify>
          )}
          {enableAdvancedChat && (
            <div className="add-reaction-wrapper">
              <Button
                className={classNames('add-reaction', {
                  visible: showReactions,
                })}
                type="button"
                onClick={handleAddReactionClick}
                title={t('chat.addReaction')}
                variant="secondary"
              >
                <CloseIcon size={16} rotate={45} variant="thick" />
                <VisuallyHidden>{t('chat.addReaction')}</VisuallyHidden>
              </Button>
            </div>
          )}
        </div>
        {enableAdvancedChat && (reactionsCount > 0 || showReactions) && (
          <div className="reactions">
            <ChatReactions
              message={message}
              onReact={handleReaction}
              showAll={showReactions}
            />
          </div>
        )}
        {showSender && (
          <div className="sender">
            <Text color="muted" variant={textVariantSmall}>
              {message.local
                ? message.name !== t('people.guest')
                  ? t('people.you')
                  : t('people.youGuest')
                : message.name || t('people.guest')}{' '}
              <Text color="muted" El="span" variant={textVariantSmall}>
                {message.received.toLocaleTimeString()}
              </Text>
            </Text>
          </div>
        )}
        <style jsx>{`
          .msg {
            background: ${colors.backgroundAccent};
            border-radius: 8px;
            color: ${colors.baseText};
            padding: 8px 12px;
            position: relative;
            word-break: break-word;
          }
          .chat-message {
            padding-top: 4px;
          }
          .sender {
            padding-bottom: 8px;
          }
          @media ${mediaQueries.coarse} {
            .msg {
              font-size: ${fontSizes.large};
              padding: 12px;
              line-height: ${lineHeights.large};
            }
          }
          .msg.isLocal {
            background: ${colors.accent};
            color: ${colors.accentText};
          }
          .msg.gif {
            background: transparent;
            padding: 0;
          }
          .msg :global(.powered-by-giphy) {
            align-items: center;
            display: flex;
            gap: 4px;
            justify-content: flex-start;
          }
          .msg :global(.powered-by-giphy picture),
          .msg :global(.powered-by-giphy img) {
            display: block;
          }
          .add-reaction-wrapper {
            right: -8px;
            position: absolute;
            top: -8px;
          }

          .msg :global(.add-reaction) {
            border-radius: 100%;
            opacity: 0;
            padding: 4px;
            transition: opacity 150ms ease;
          }
          .msg:hover :global(.add-reaction),
          .msg :global(.add-reaction:focus),
          .msg :global(.add-reaction.visible) {
            opacity: 1;
          }
          .reactions {
            display: block;
            margin: 2px 0;
            pointer-events: none;
            transition: opacity 200ms ease;
          }
          .sender {
            margin-top: 4px;
          }
          .sender:not(:last-child) {
            margin-bottom: 8px;
          }
        `}</style>
      </div>
    );
  }
);
ChatMessage.displayName = 'ChatMessage';
